import { useEffect, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useLocation } from "wouter";
import Header from "@/components/Header";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { useToast } from "@/hooks/use-toast";
import { ArrowLeft, Loader2 } from "lucide-react";
import type { NotificationPrefs } from "@shared/schema";

const DISCRETIONARY: {
  key: keyof NotificationPrefs;
  label: string;
  description: string;
}[] = [
  {
    key: "ripeReady",
    label: "Reservation ripe",
    description:
      "When something you reserved is ready and the balance has been charged.",
  },
  {
    key: "itemSaved",
    label: "Saved listings",
    description: "When another buyer saves one of your items (sellers).",
  },
  {
    key: "followActivity",
    label: "Sellers you follow",
    description: "When sellers you follow post a new listing.",
  },
  {
    key: "gardenReminders",
    label: "Garden & growth reminders",
    description:
      "Watering schedules, weekly progress photo reminders, plant care nudges.",
  },
  {
    key: "referrals",
    label: "Referrals & rewards",
    description: "When friends sign up with your code or you unlock a tier.",
  },
  {
    key: "deliveryInvites",
    label: "Become-a-driver invites",
    description:
      "When a neighbor near you needs a delivery and no driver is available. Your own order and delivery updates are always on.",
  },
];

const ALWAYS_ON = [
  "Order placed, paid, prepared, ready, out for delivery, delivered",
  "Cancellation requests + resolutions",
  "Delivery requests, accepts, pickups, drop-offs (drivers)",
  "Direct messages from buyers / sellers / drivers",
];

export default function NotificationSettings() {
  const [, setLocation] = useLocation();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [prefs, setPrefs] = useState<NotificationPrefs>({});

  const { data: serverPrefs, isLoading } = useQuery<NotificationPrefs>({
    queryKey: ["/api/account/notification-prefs"],
    queryFn: async () => {
      const res = await fetch("/api/account/notification-prefs", {
        credentials: "include",
      });
      if (!res.ok) throw new Error(await res.text());
      return res.json();
    },
  });

  useEffect(() => {
    if (serverPrefs) setPrefs(serverPrefs);
  }, [serverPrefs]);

  const saveMutation = useMutation({
    mutationFn: async (next: NotificationPrefs) => {
      const res = await fetch("/api/account/notification-prefs", {
        method: "PUT",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(next),
      });
      if (!res.ok) throw new Error(await res.text());
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/account/notification-prefs"] });
      toast({ title: "Preferences saved" });
    },
    onError: (err: Error) => {
      toast({ title: "Save failed", description: err.message, variant: "destructive" });
    },
  });

  const toggle = (key: keyof NotificationPrefs) => {
    setPrefs((current) => {
      const isOn = current[key] !== false;
      return { ...current, [key]: !isOn };
    });
  };

  const handleSave = () => {
    saveMutation.mutate(prefs);
  };

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <Header cartCount={0} onCartClick={() => {}} />
      <main className="flex-1">
        <div className="container mx-auto max-w-xl px-4 py-5">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setLocation("/account")}
            className="mb-3"
          >
            <ArrowLeft className="mr-1 h-4 w-4" />
            Back
          </Button>

          <h1 className="mb-4 text-xl font-bold">Notifications</h1>

          {isLoading ? (
            <div className="flex items-center gap-2 text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />
              Loading…
            </div>
          ) : (
            <div className="space-y-4">
              <Card>
                <CardContent className="pt-5 space-y-3">
                  <div className="text-sm font-semibold">Optional alerts</div>
                  <p className="text-xs text-muted-foreground">
                    Toggle these off if you don't want them. They default to on.
                  </p>
                  {DISCRETIONARY.map((entry) => {
                    const enabled = prefs[entry.key] !== false;
                    return (
                      <button
                        key={entry.key}
                        type="button"
                        onClick={() => toggle(entry.key)}
                        className="flex w-full items-start justify-between gap-3 rounded-lg border p-3 text-left transition hover:bg-muted/50"
                      >
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium">{entry.label}</div>
                          <div className="text-xs text-muted-foreground">
                            {entry.description}
                          </div>
                        </div>
                        <div
                          className={`mt-0.5 inline-flex h-6 w-10 shrink-0 items-center rounded-full px-0.5 transition-colors ${
                            enabled ? "bg-primary" : "bg-muted"
                          }`}
                        >
                          <span
                            className={`h-5 w-5 rounded-full bg-white shadow transition-transform ${
                              enabled ? "translate-x-4" : "translate-x-0"
                            }`}
                          />
                        </div>
                      </button>
                    );
                  })}
                </CardContent>
              </Card>

              <Card>
                <CardContent className="pt-5 space-y-2">
                  <div className="text-sm font-semibold">Always on</div>
                  <p className="text-xs text-muted-foreground">
                    These can't be muted — they're how the marketplace runs.
                  </p>
                  <ul className="text-xs text-muted-foreground space-y-1.5 mt-2">
                    {ALWAYS_ON.map((line) => (
                      <li key={line} className="flex gap-2">
                        <span className="text-primary">•</span>
                        <span>{line}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>

              <Button
                onClick={handleSave}
                disabled={saveMutation.isPending}
                className="w-full"
              >
                {saveMutation.isPending ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : null}
                Save preferences
              </Button>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
